import { useMutation, useQuery } from '@tanstack/react-query';
import httpClient from '~/lib/client/httpClient';
import { queryClient } from '~/lib/react-query';
import { BroadcastEvents, channel } from '~/lib/broadcast';
import Cookies from 'js-cookie';
import {
  CUSTOM_TOKEN_COOKIES_KEY,
  RF_CUSTOM_TOKEN_COOKIES_KEY,
  CookieOptions,
} from '~/constants/auth';
import { errorAlert, showAlert } from '~/components/common/Alert';
import { FirebaseError } from 'firebase/app';
import { SSO_AUTH_ENDPOINT } from '~/config';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { queryKeys } from './queryKeys';
import endpoints from './endpoints';
import {
  getCurrentUser,
  logout,
  login,
  formatFirebaseError,
} from '../firebase';

export const getAccounts = (): Promise<{
  email?: string;
}> => httpClient.json.get(endpoints.auth.getUser);

export const getListUser = (): Promise<any> =>
  httpClient.json.get(endpoints.auth.getListUser);

export const signIn = async ({ email, password }) => login({ email, password });

export const signOut = async () => logout();

export const signOutSSO = async () => {
  try {
    await httpClient.json.post(
      `${endpoints.sso.logout}`,
      {},
      {
        baseURL: SSO_AUTH_ENDPOINT,
        withCredentials: true,
      },
    );
  } catch (error) {
    console.log(error);
  }
  return logout();
};

export const checkAuth = async () =>
  httpClient.json.post(
    `${endpoints.sso.checkAuth}`,
    {},
    {
      baseURL: SSO_AUTH_ENDPOINT,
      withCredentials: true,
    },
  );

export const generateCustomTokenSSO = () =>
  httpClient.json.post(
    `${endpoints.sso.auth}`,
    {},
    {
      baseURL: SSO_AUTH_ENDPOINT,
      withCredentials: true,
    },
  );

export const useGetAccounts = ({
  enabled = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess = (res: { email: string }) => {},
  onError = (error?: any) => {},
}: {
  enabled?: boolean;
  onSuccess?: (res: { email: string }) => void;
  onError?: (error?: any) => void;
} = {}) =>
  useQuery({
    queryKey: [queryKeys.auth.account],
    queryFn: () => getAccounts(),
    initialData: {},
    onSuccess,
    onError,
    enabled,
    retry: false,
  });

export const handleLoginSuccess = async (options?: {
  isInvalidateQueries?: boolean;
  isShowAlert?: boolean;
}) => {
  await generateCustomTokenSSO();

  if (options.isInvalidateQueries) {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.auth.account],
    });
  }
  channel.postMessage({
    type: BroadcastEvents.RefreshOtherPageWhenLogged,
  });
};

export const useLogin = ({
  options = {
    isInvalidateQueries: true,
    isShowAlert: true,
  },
  onSuccess,
  onError,
}: {
  onSuccess?: (resp: any) => void;
  onError?: (errMsg: any, error?: any) => void;
  options?: {
    isInvalidateQueries?: boolean;
    isShowAlert?: boolean;
  };
}) =>
  useMutation({
    mutationFn: signIn,
    onSuccess: async () => {
      const user: any = await getCurrentUser();
      await handleLoginSuccess(options);
      onSuccess?.(user);
    },
    onError: (errors: FirebaseError) => {
      const errorMsg = formatFirebaseError(errors);
      if (options.isShowAlert) {
        showAlert(errorAlert(errorMsg));
      }
      onError?.(errorMsg, errors);
    },
  });

export const useLogout = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (resp: any) => void;
  onError?: (err: any) => void;
} = {}) => {
  const isNewSSO = useFeatureIsOn(
    'acd-11071-cookie-attributes-not-set-optimal-security',
  );
  const fn = isNewSSO ? signOutSSO : signOut;
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      httpClient.json.clearToken();
      if (!isNewSSO) {
        Cookies.remove(CUSTOM_TOKEN_COOKIES_KEY, CookieOptions);
        Cookies.remove(RF_CUSTOM_TOKEN_COOKIES_KEY, CookieOptions);
      }
      channel.postMessage({
        type: BroadcastEvents.RefreshOtherPageWhenLogout,
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError,
  });
};

export const useCheckSessionSSO = (params) => {
  const { onSuccess, onError, enabled } = params;
  return useQuery(['generateCustomToken'], () => checkAuth(), {
    refetchInterval: 50 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
    onSuccess: (resp: any) => {
      onSuccess?.(resp);
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useGetListUser = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess = (res) => {},
  onError = () => {},
}) =>
  useQuery({
    queryKey: [queryKeys.auth.listUser],
    queryFn: () => getListUser(),
    select: (res) => res?.data || [],
    onSuccess,
    onError,
  });
